<template>
  <div
    ref="root"
    class="ss-pop"
    :class="[`ss-pop--${kind}`, { 'is-sheet': sheet }]"
    role="dialog"
    :aria-label="title"
    tabindex="-1"
    @keydown.esc.stop="close"
  >
    <div v-if="sheet" class="ss-pop__grab" aria-hidden="true"></div>
    <div class="ss-pop__h">
      <h4>{{ title }}</h4>
      <button type="button" class="ui-icon-btn ui-icon-btn--plain ui-icon-btn--sm" :aria-label="$t('close')" @click="close">
        <v-icon size="16">mdi-close</v-icon>
      </button>
    </div>

    <div class="ss-pop__b">
      <!-- nesnimljene izmjene -->
      <template v-if="kind === 'changes'">
        <div v-if="!ctl.changes.length" class="ss-pop__e">{{ $t("settings_shell.changes.empty") }}</div>
        <div v-else class="ss-chl">
          <div v-for="group in changeGroups" :key="group.key">
            <div class="ss-chg-t">{{ group.title }}</div>
            <div v-for="change in group.items" :key="change.path" class="ss-chrow">
              <div class="ss-chrow__t">
                <b>{{ ctl.labelFor(change.path) }}</b>
                <span>
                  <s>{{ ctl.formatValue(change.path, change.from) }}</s>
                  →
                  <em>{{ ctl.formatValue(change.path, change.to) }}</em>
                </span>
              </div>
              <button type="button" class="ui-btn ui-btn--sm ui-btn--ghost" @click="ctl.revert(change.path)">
                <v-icon size="14">mdi-undo</v-icon>
                {{ $t("settings_shell.changes.revert") }}
              </button>
            </div>
          </div>
        </div>
      </template>

      <!-- brzo podešavanje: izbor -->
      <div v-else-if="kind === 'quick'" class="ss-menu">
        <button type="button" class="ss-mi" @click="ctl.ui.pop = 'cycle'">
          <v-icon size="18">mdi-calendar-sync-outline</v-icon>
          <span>
            <b>{{ $t("settings_shell.quick.cycle") }}</b>
            <em>{{ $t("settings_shell.quick.cycle_d") }}</em>
          </span>
        </button>
        <button type="button" class="ss-mi" @click="ctl.ui.pop = 'uniform'">
          <v-icon size="18">mdi-clock-outline</v-icon>
          <span>
            <b>{{ $t("settings_shell.quick.uniform") }}</b>
            <em>{{ $t("settings_shell.quick.uniform_d") }}</em>
          </span>
        </button>
        <button type="button" class="ss-mi" @click="ctl.copyDay()">
          <v-icon size="18">mdi-content-copy</v-icon>
          <span>
            <b>{{ $t("settings_shell.quick.copy", { day: selectedDayName }) }}</b>
            <em>{{ $t("settings_shell.quick.copy_d") }}</em>
          </span>
        </button>
      </div>

      <!-- sedmični ciklus -->
      <template v-else-if="kind === 'cycle'">
        <p class="ss-pop__d">{{ $t("settings_shell.cycle.intro") }}</p>
        <div class="ss-pf">
          <label class="ss-lbl" for="ss-cy-h">{{ $t("settings_shell.cycle.ends") }}</label>
          <select id="ss-cy-h" class="ss-select" @change="cy.h = Number($event.target.value)">
            <option v-for="(day, i) in days" :key="day" :value="i" :selected="i === cy.h">{{ $t(day) }}</option>
          </select>
        </div>
        <div class="ss-pf ss-pf--2">
          <div>
            <label class="ss-lbl" for="ss-cy-o">{{ $t("settings_shell.cycle.opens") }}</label>
            <select id="ss-cy-o" class="ss-select" @change="cy.o = Number($event.target.value)">
              <option v-for="(day, i) in days" :key="day" :value="i" :selected="i === cy.o">{{ $t(day) }}</option>
            </select>
          </div>
          <set-time local path="cycle.time" :label="$t('settings_shell.cycle.at')" />
        </div>
        <div class="ss-cy-s">
          <span>{{ $t("settings_shell.cycle.before_line", { date: cycleDate(cycleRows[cy.o].before) }) }}</span>
          <span>{{ $t("settings_shell.cycle.after_line", { date: cycleDate(cycleRows[cy.o].after) }) }}</span>
        </div>
        <div class="ss-cy-p" :title="$t('settings_shell.cycle.preview_title')">
          <span v-for="(rule, i) in cycleRows" :key="i">
            <b>{{ dayShort[i] }}</b>
            {{ rule.before }}{{ rule.before === rule.after ? "" : `/${rule.after}` }}
          </span>
        </div>
      </template>

      <!-- isti rok za sve dane -->
      <template v-else-if="kind === 'uniform'">
        <p class="ss-pop__d">{{ $t("settings_shell.uniform.intro") }}</p>
        <div class="ss-pf ss-pf--3">
          <set-time local path="uniform.time" :label="$t('settings_shell.dl.deadline')" />
          <set-stepper local path="uniform.before" :label="$t('settings_shell.dl.before')" :unit="$t('settings_shell.dl.days_unit')" />
          <set-stepper local path="uniform.after" :label="$t('settings_shell.dl.after')" :unit="$t('settings_shell.dl.days_unit')" />
        </div>
      </template>
    </div>

    <div v-if="kind !== 'quick' && (kind !== 'changes' || ctl.changes.length)" class="ss-pop__f">
      <template v-if="kind === 'changes'">
        <button type="button" class="ui-btn ui-btn--sm" @click="ctl.revertAll()">
          <v-icon size="14">mdi-undo</v-icon>
          {{ $t("settings_shell.changes.revert_all") }}
        </button>
        <button type="button" class="ui-btn ui-btn--sm ui-btn--primary" :disabled="ctl.ui.saving" @click="ctl.save()">
          {{ $t("settings_shell.save") }}
        </button>
      </template>
      <template v-else>
        <button type="button" class="ui-btn ui-btn--sm" @click="close">{{ $t("settings_shell.cancel") }}</button>
        <button
          type="button"
          class="ui-btn ui-btn--sm ui-btn--primary"
          @click="kind === 'cycle' ? ctl.applyCycle() : ctl.applyUniform()"
        >
          {{ $t("settings_shell.quick.apply_all") }}
        </button>
      </template>
    </div>
  </div>
</template>

<script>
import { DAYS, cycleRules } from "../settingsLogic";
import { SCREENS } from "../settingsRegistry";
import SetTime from "./SetTime.vue";
import SetStepper from "./SetStepper.vue";

/**
 * Sadržaj iskakanja (računar) i donje kartice (telefon): izmjene koje nisu snimljene, brzo podešavanje rokova,
 * sedmični ciklus i isti rok za sve dane. Ko ga otvara postavlja `ctl.ui.pop`.
 */
export default {
  name: "SetPanel",
  components: { SetTime, SetStepper },
  inject: { ctl: "settingsCtl" },
  props: {
    kind: { type: String, required: true },
    sheet: { type: Boolean, default: false },
  },
  data: () => ({ days: DAYS }),
  computed: {
    title() {
      return this.$t(`settings_shell.panel.${this.kind}`);
    },
    cy() {
      return this.ctl.ui.cycle;
    },
    cycleRows() {
      return cycleRules(this.cy.h, this.cy.o, this.cy.time || "12:00");
    },
    selectedDayName() {
      return this.$t(DAYS[this.ctl.ui.sim.days.day]);
    },
    dayShort() {
      return DAYS.map((_, i) => this.localeDate(this.dateAt(i), { weekday: "short" }));
    },
    changeGroups() {
      const order = SCREENS.map((screen) => screen.key);
      return order
        .filter((key) => (this.ctl.changesByScreen[key] || []).length)
        .map((key) => ({
          key,
          title: this.ctl.screenTitle(key),
          items: this.ctl.changesByScreen[key],
        }));
    },
  },
  mounted() {
    this.$nextTick(() => this.$refs.root && this.$refs.root.focus({ preventScroll: true }));
  },
  beforeDestroy() {
    const opener = document.querySelector(`[data-open="${this.kind}"]`);
    if (opener && (!document.activeElement || document.activeElement === document.body)) {
      opener.focus({ preventScroll: true });
    }
  },
  methods: {
    close() {
      this.ctl.ui.pop = null;
    },
    dateAt(offset) {
      const date = new Date();
      date.setHours(12, 0, 0, 0);
      date.setDate(date.getDate() - ((date.getDay() + 6) % 7) + offset);
      return date;
    },
    /** Datum do kog se bira, računat od dana otvaranja nove sedmice u ovoj sedmici. */
    cycleDate(ahead) {
      const date = this.dateAt(this.cy.o + ahead);
      return `${this.$t(DAYS[(date.getDay() + 6) % 7])}, ${this.localeDate(date, { day: "numeric", month: "long" })}`;
    },
  },
};
</script>
